import { AlertTriangle, CheckCircle2, Loader2, RefreshCw } from "lucide-react";
import type React from "react";
import { cn } from "../../lib/utils";
import type {
	ComprehensiveScenario,
	ScenarioFreshness,
} from "../../types/scenarios";
import { Badge } from "../ui/badge";

/**
 * Derives the freshness value to display for a scenario. Parked and archived
 * scenarios can never be "Updating" — they only ever show Live or Stale.
 */
export function resolveFreshness(
	scenario: Pick<ComprehensiveScenario, "status" | "freshness">,
): ScenarioFreshness {
	const freshness = scenario.freshness ?? "live";
	if (
		(scenario.status === "parked" || scenario.status === "archived") &&
		freshness === "updating"
	) {
		return "live";
	}
	return freshness;
}

const FRESHNESS_CONFIG: Record<
	ScenarioFreshness,
	{ label: string; className: string; icon: React.ElementType; spin?: boolean }
> = {
	live: {
		label: "Live",
		className:
			"bg-[var(--accent-success-50)] text-[var(--accent-success-900)] border-[var(--accent-success-500)]",
		icon: CheckCircle2,
	},
	stale: {
		label: "Stale",
		className:
			"bg-[var(--accent-warning-50)] text-[var(--accent-warning-900)] border-[var(--accent-warning-500)]",
		icon: RefreshCw,
	},
	updating: {
		label: "Updating",
		className:
			"bg-[var(--accent-info-50)] text-[var(--accent-info-900)] border-[var(--accent-info-500)]",
		icon: Loader2,
		spin: true,
	},
	error: {
		label: "Error",
		className:
			"bg-[var(--accent-destructive-50)] text-[var(--accent-destructive-900)] border-[var(--accent-destructive-500)]",
		icon: AlertTriangle,
	},
};

interface ScenarioFreshnessBadgeProps {
	scenario: Pick<ComprehensiveScenario, "status" | "freshness">;
	className?: string;
}

/**
 * Freshness badge shown on scenario cards and the detail header. Separate from
 * the workflow status badge — reflects the scenario's position relative to the
 * latest base forecast (Live / Stale / Updating / Error).
 */
export const ScenarioFreshnessBadge: React.FC<ScenarioFreshnessBadgeProps> = ({
	scenario,
	className,
}) => {
	const freshness = resolveFreshness(scenario);
	const config = FRESHNESS_CONFIG[freshness];
	const Icon = config.icon;
	return (
		<Badge
			variant="outline"
			className={cn(
				"text-xs font-medium gap-1 items-center",
				config.className,
				className,
			)}
			aria-label={`Freshness: ${config.label}`}
		>
			<Icon className={cn("h-3 w-3", config.spin && "animate-spin")} />
			{config.label}
		</Badge>
	);
};
